import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Contrato } from 'src/app/interfaces';
import { NewContractsService } from 'src/app/services/new-contracts.service';
import { ColLabel, IButtonTable } from '../table/table.interface';
import { Router } from '@angular/router';
import { TracingService } from 'src/app/modules/tracing/services/tracing.service';
import { UtilService } from 'src/app/services/util.service';
import { AuthService } from 'src/app/auth';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
@Component({
  selector: 'app-new-contracts',
  templateUrl: './new-contracts.component.html'
})
export class NewContractsComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  public isLoading = true;
  private isExternal: boolean;
  public numIncidencia: number;
  public numBorrador: number;
  public numPendienteFirma: number;
  public numRechazoAcciona: number;
  public numRachazoAtr: number;
  public contratos: Contrato[];
  public auxContratos: Contrato[];
  public colsTable: ColLabel[] = [];
  private isUserSpanish = true;

  constructor(
    private newContractsService: NewContractsService,
    private tracingService: TracingService,
    public router: Router,
    private utilService: UtilService,
    private authService: AuthService,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService
  ) { }

  ngOnInit(): void {
    this.isUserSpanish = this.authService.getUserData().country === 'ES';
    this.subscribeLanguajeChanges();
    this.buildColumns();
    this.getNewContracts();
    this.isExternal = this.authService.getRole().suffix === '-pymes';
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private subscribeLanguajeChanges(): void {
    this.i18NextService.events.languageChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.auxContratos) {
        this.mapContracts();
      }
    });
  }

  private getNewContracts(): void {
    this.newContractsService.getNewContracts(this.isUserSpanish ? 'pymes.getNewContracts' : 'pymes.getNewContractsPT').pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.numIncidencia = res.data?.numIncidencia;
      this.numBorrador = res.data?.numBorrador;
      this.numPendienteFirma = res.data?.numPendienteFirma;
      this.numRechazoAcciona = res.data?.numRechazoAcciona;
      this.numRachazoAtr = res.data?.numRachazoAtr;
      this.auxContratos = res.data?.contratos;
      this.mapContracts();
      this.isLoading = false;
    }, error => {
      this.isLoading = false;
      this.utilService.toastErrors(error);
    });
  }

  private mapContracts(): void {
    this.contratos = this.auxContratos?.map(value => {
      value.cups = value.multipunto ? this.i18NextService.t('NEW_CONTRACTS.MULTIPOINT.MULTI_POINT') : value.cups;
      if (this.authService.getOnlyCanRead()) {
        value.hidden = !(value.isBorrador || value.renovacion || value.multipunto || value.cambioProducto);
      } else {
        value.hidden = true;
      }
      return value;
    });
  }

  private buildColumns(): void {
    this.colsTable = [
      {
        label: 'NEW_CONTRACTS.BUSINESS_SIGN',
        field: ['nombreRazonSocial', 'fechaFirma'],
        fieldClass: ['', 'date'],
        action: ''
      },
      {
        label: 'NEW_CONTRACTS.STATE',
        field: ['estado'],
        fieldClass: [''],
        action: ''
      },
      {
        label: 'NEW_CONTRACTS.CIF',
        field: ['cif'],
        fieldClass: [''],
        action: ''
      },
      {
        label: 'NEW_CONTRACTS.CUPS',
        field: ['cups'],
        fieldClass: [''],
        action: ''
      },
      {
        label: 'NEW_CONTRACTS.OPINION',
        field: ['codOpinionCredito'],
        fieldClass: ['opinionCredito'],
        action: ''
      },
      {
        label: '',
        field: ['idBorrador'],
        fieldClass: ['buttonFixed'],
        action: 'goToClicked',
        nameIcon: 'arrow',
        hiddenIcon: ['hidden']
      }
    ];
  }

  public handleButton(event: IButtonTable): void {
    if (event.data.isBorrador) {
      this.navigateDraft(event);
    } else if (event.data.codEstado === '300000001') {
      this.navigateSign(event);
    } else {
      this.tracingService.setContractDetail(event.data, this.isExternal);
      this.router.navigate(['/tracing/contract-detail'], { queryParams: { tab: 'home-pymes', idCodigoCRM: event.data.idCodigoCRM } });
    }
  }

  private navigateDraft(event: IButtonTable): void {
    const queryParams = { queryParams: { idDraft: event.data.idBorrador } };
    if (event.data.renovacion) {
      if (event.data.multipunto) {
        this.router.navigate(['renovate-contract-multipoint'], queryParams);
      } else {
        this.router.navigate(['renovate-contract-pymes'], queryParams);
      }
    } else if (event.data.cambioProducto) {
      if (event.data.multipunto) {
        this.router.navigate(['change-product-multipoint'], queryParams);
      } else {
        this.router.navigate(['change-product'], queryParams);
      }
    } else if (event.data.multipunto) {
      this.router.navigate(['/new-contract-pymes-multipoint'], queryParams);
    } else {
      this.router.navigate(['/draft'], queryParams);
    }
  }

  private navigateSign(event: IButtonTable): void {
    if (event.data.renovacion) {
      if (event.data.multipunto) {
        this.router.navigate(['renovate-contract-multipoint'], {
          queryParams: {
            idContract: event.data.codigoContrato,
            codeSocialReason: event.data.nombreRazonSocial,
            disabledAddSepa: !event.data.codigoSEPA,
            bankAccountsSepa: event.data.cuentasCorrientesSEPA,
            hasChangedDni: event.data.hasChangedDni,
            hasChangedBankAccount: event.data.cuentasCorrientesSEPA?.length > 0
          }
        });
      } else {
        this.router.navigate(['renovate-contract-pymes'], {
          queryParams: {
            idContract: event.data.codigoContrato,
            codeSocialReason: event.data.nombreRazonSocial,
            disabledAddSepa: !event.data.codigoSEPA,
            hasChangedDni: event.data.hasChangedDni,
            hasChangedBankAccount: event.data.hasChangedBankAccount,
          }
        });
      }
    } else if (event.data.cambioProducto) {
      if (event.data.multipunto) {
        this.router.navigate(['change-product-multipoint'], {
          queryParams: {
            idContract: event.data.codigoContrato,
            codeSocialReason: event.data.nombreRazonSocial,
            disabledAddSepa: !event.data.codigoSEPA,
            bankAccountsSepa: event.data.cuentasCorrientesSEPA,
            hasChangedDni: event.data.hasChangedDni,
            hasChangedBankAccount: event.data.cuentasCorrientesSEPA?.length > 0
          }
        });
      } else {
        this.router.navigate(['change-product'], {
          queryParams: {
            idContract: event.data.codigoContrato,
            codeSocialReason: event.data.nombreRazonSocial,
            disabledAddSepa: !event.data.codigoSEPA,
            hasChangedDni: event.data.hasChangedDni,
            hasChangedBankAccount: event.data.hasChangedBankAccount
          }
        });
      }
    } else if (event.data.multipunto) {
      this.router.navigate(['/new-contract-pymes-multipoint'], {
        queryParams: {
          codContract: event.data.codigoContrato,
          codeSocialReason: event.data.nombreRazonSocial,
          disabledAddSepa: !event.data.codigoSEPA,
          bankAccountsSepa: event.data.cuentasCorrientesSEPA
        }
      });
    } else {
      this.router.navigate(['/draft'], {
        queryParams: {
          codigoContrato: event.data.codigoContrato,
          codigoRazonSocial: event.data.nombreRazonSocial,
          nombreRazonSocial: event.data.nombreRazonSocial,
          disabledAddSepa: !event.data.codigoSEPA
        }
      });
    }
  }

  public handleNavigate(): void {
    this.router.navigate(['tracing'], { queryParams: { tab: 1 } });
  }
}
